import { Link, useLocation, useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useUser } from "../lib/UserContext";
import { useEffect, useState, useCallback, useRef } from "react";
import toast from "react-hot-toast";
import {
  MessageSquare, Brain, BarChart2, Bell, User, Users, Receipt,
  Building2, FileText, CalendarDays, FileSignature, UserPlus,
  FlaskConical, Zap, Database, MessageCircle, LogOut, LayoutTemplate, Settings as SettingsIcon, Mail, SearchCheck,
  ShieldCheck, Radar,
} from "lucide-react";
import { usePermissions } from "../lib/usePermission";
import { useStaff } from "../lib/StaffContext";
import { getSidebarIdentityLabel } from "../lib/sidebarIdentity";
import { buildClinicNavKeys, buildClinicSettingsPath } from "../lib/clinicSettings.mjs";

type ClinicStatus = {
  status: "OPEN" | "CLOSED";
  source: "FORCED" | "SCHEDULE" | "HOLIDAY";
  label: string;
};

type OverrideStatus = "OPEN" | "CLOSED";

let sidebarRealtimeInstanceCounter = 0;

function buildSidebarRealtimeKey(variant: "desktop" | "mobile") {
  sidebarRealtimeInstanceCounter += 1;
  return `${variant}-${sidebarRealtimeInstanceCounter}`;
}

export default function Sidebar({
  activeDoctor,
  onClose,
  variant = "desktop",
  enableExperienceIntelligence = false,
}: {
  activeDoctor?: any;
  onClose?: () => void;
  variant?: "desktop" | "mobile";
  enableExperienceIntelligence?: boolean;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const user = useUser();
  const permissions = usePermissions();
  const { activeStaff } = useStaff();
  const sidebarIdentityLabel = getSidebarIdentityLabel(activeStaff, activeDoctor, user);
  const [sidebarRealtimeKey] = useState(() => buildSidebarRealtimeKey(variant));
  const [loading, setLoading] = useState(false);
  const [hasPendingExamples, setHasPendingExamples] = useState(false);
  const [tasksCount, setTasksCount] = useState(0);
  const [clinicStatus, setClinicStatus] = useState<ClinicStatus | null>(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [overrideStatus, setOverrideStatus] = useState<OverrideStatus>("OPEN");
  const [overrideUntil, setOverrideUntil] = useState("");
  const [overrideReason, setOverrideReason] = useState("");
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [botDisabledClinic, setBotDisabledClinic] = useState(false);
  const [botToggleLoading, setBotToggleLoading] = useState(false);

  /* ───────────────────────────────────────────────
     🔁 Gestion du compteur de tâches assignées
  ─────────────────────────────────────────────── */
  const fetchTasksDebounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchTasks = useCallback(async () => {
    if (!user) return;
    const { count } = await supabase
      .from("conversations")
      .select("id", { count: "exact", head: true })
      .eq("assigned_to", user.id)
      .eq("assignment_seen", false);
    setTasksCount(count || 0);
  }, [user]);

  const fetchTasksDebounced = useCallback(() => {
    if (fetchTasksDebounceTimer.current) clearTimeout(fetchTasksDebounceTimer.current);
    fetchTasksDebounceTimer.current = setTimeout(() => fetchTasks(), 600);
  }, [fetchTasks]);

  useEffect(() => {
    if (!user) return;
    fetchTasks();

    const channel = supabase
      .channel(`sidebar-${sidebarRealtimeKey}-conversations-assignments-${user.id}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "conversations", filter: `assigned_to=eq.${user.id}` },
        () => fetchTasksDebounced()
      )
      .subscribe();

    return () => {
      if (fetchTasksDebounceTimer.current) clearTimeout(fetchTasksDebounceTimer.current);
      supabase.removeChannel(channel);
    };
  }, [user, fetchTasks, fetchTasksDebounced, sidebarRealtimeKey]);

  /* ───────────────────────────────────────────────
     📘 Vérification des exemples Knowledge Base
  ─────────────────────────────────────────────── */
  useEffect(() => {
    if (!user) return;
    const fetchPending = async () => {
      let query = supabase.from("validated_examples").select("id").eq("is_validated", false);
      if (user.role !== "admin") query = query.eq("praticien_id", user.id);
      const { data } = await query;
      setHasPendingExamples(!!data && data.length > 0);
    };
    fetchPending();

    const channel = supabase
      .channel(`sidebar-${sidebarRealtimeKey}-validated-examples-${user.id}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "validated_examples" },
        () => fetchPending()
      )
      .subscribe();

    const handler = () => fetchPending();
    window.addEventListener("examples-updated", handler);

    return () => {
      supabase.removeChannel(channel);
      window.removeEventListener("examples-updated", handler);
    };
  }, [user, sidebarRealtimeKey]);

  /* ───────────────────────────────────────────────
     🏥 Statut de la clinique + état du bot
  ─────────────────────────────────────────────── */
  const fetchClinicInfo = useCallback(async () => {
    if (!user) return;
    try {
      setStatusLoading(true);
      const session = await supabase.auth.getSession();
      const token = session.data.session?.access_token;
      if (!token) return;

      const res = await fetch(`${import.meta.env.VITE_API_URL}/clinic/info`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) throw new Error("Failed to load clinic info");
      const data = await res.json();
      setClinicStatus({ status: data.status, source: data.source, label: data.label });
      setBotDisabledClinic(Boolean(data.bot_disabled_clinic));
    } catch (err) {
      console.error("[CLINIC_INFO][ERROR]", err);
    } finally {
      setStatusLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchClinicInfo();
  }, [fetchClinicInfo]);

  const canToggleClinicBot =
    user?.role === "admin" || user?.role === "clinic" || user?.role === "praticien";

  const handleToggleClinicBot = async () => {
    if (!canToggleClinicBot || botToggleLoading) return;

    const previous = botDisabledClinic;
    const nextValue = !previous;
    setBotDisabledClinic(nextValue);

    try {
      setBotToggleLoading(true);
      const session = await supabase.auth.getSession();
      const token = session.data.session?.access_token;
      if (!token) throw new Error("Missing auth token");

      const res = await fetch(`${import.meta.env.VITE_API_URL}/clinic/bot`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ bot_disabled_clinic: nextValue }),
      });

      if (!res.ok) throw new Error("Failed to update clinic bot status");

      const data = await res.json();
      setBotDisabledClinic(Boolean(data?.bot_disabled_clinic));
      toast.success(data?.bot_disabled_clinic ? "Bot disabled clinic-wide" : "Bot enabled");
    } catch (err) {
      setBotDisabledClinic(previous);
      console.error("[BOT][CLINIC][PATCH][ERROR]", err);
      toast.error("Failed to update bot status");
    } finally {
      setBotToggleLoading(false);
    }
  };

  const openStatusModal = () => {
    if (!clinicStatus) return;
    setOverrideStatus(clinicStatus.status);
    setOverrideUntil("");
    setOverrideReason("");
    setStatusModalOpen(true);
  };

  const updateClinicStatus = async (payload: Record<string, any>) => {
    try {
      setStatusUpdating(true);
      const session = await supabase.auth.getSession();
      const token = session.data.session?.access_token;
      if (!token) return;

      await fetch(`${import.meta.env.VITE_API_URL}/clinic/status`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      await fetchClinicInfo();
      setStatusModalOpen(false);
    } catch (err) {
      console.error("[CLINIC_STATUS][UPDATE][ERROR]", err);
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleForceStatus = () => {
    if (!overrideUntil) {
      alert("Please select an end date and time.");
      return;
    }
    updateClinicStatus({
      forced_status: overrideStatus,
      forced_until: new Date(overrideUntil).toISOString(),
      reason: overrideReason || null,
    });
  };

  const handleRevertSchedule = () => {
    updateClinicStatus({ forced_status: null, forced_until: null, reason: null });
  };

  const badgeColors = (() => {
    if (clinicStatus?.source === "FORCED" && clinicStatus.status === "CLOSED")
      return { container: "bg-red-100 text-red-800 border-red-200", dot: "bg-red-500" };
    if (clinicStatus?.source === "HOLIDAY")
      return { container: "bg-amber-100 text-amber-800 border-amber-200", dot: "bg-amber-500" };
    if (clinicStatus?.status === "OPEN")
      return { container: "bg-green-100 text-green-800 border-green-200", dot: "bg-green-500" };
    return { container: "bg-gray-100 text-gray-700 border-gray-200", dot: "bg-gray-400" };
  })();

  const handleLogout = async () => {
    setLoading(true);
    await supabase.auth.signOut();
    localStorage.removeItem("activeDoctor");
    navigate("/login");
  };

  const formatDateHuman = (iso?: string) => {
    if (!iso) return "";
    const d = new Date(iso);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = String(d.getFullYear()).slice(-2);
    const hours = String(d.getHours()).padStart(2, "0");
    const minutes = String(d.getMinutes()).padStart(2, "0");
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  };

  /* ───────────────────────────────────────────────
     🔗 Navigation items avec icônes
  ─────────────────────────────────────────────── */
  // Platform admin: fixed admin nav (unchanged)
  // Clinic praticien: dynamic nav filtered by permissions
  const clinicNavItemsByKey = {
    conversations: { name: "Chats", path: "/conversations", icon: MessageSquare },
    reminders: { name: "Reminders", path: "/reminders", icon: Bell },
    dashboard: { name: "Dashboard", path: "/dashboard", icon: BarChart2 },
    "clinic-insights": { name: "Clinic insights", path: "/clinic-insights", icon: Brain },
    knowledge: { name: "Knowledge base", path: "/knowledge", icon: Brain },
    patients: { name: "Patients directory", path: "/patients", icon: Users },
    billing: { name: "Billing & Admin", path: "/billing", icon: Receipt },
    settings: { name: "Settings", path: buildClinicSettingsPath("profile"), icon: SettingsIcon },
  } as const;

  const navItems = user?.role === "admin"
    ? [
        { name: "Mission control",    path: "/admin/mission-control", icon: Radar },
        { name: "Clients management",   path: "/clients",             icon: Building2 },
        { name: "Billing follow-up",    path: "/billing-follow-up",  icon: FileText },
        { name: "Knowledge base",       path: "/knowledge",           icon: Brain },
        { name: "Calendars management", path: "/calendars",           icon: CalendarDays },
        { name: "Contract management",  path: "/contract-generator",  icon: FileSignature },
        { name: "Signup",               path: "/signup",              icon: UserPlus },
        { name: "Plato API testing",    path: "/admin/plato",         icon: FlaskConical },
        { name: "Token Usage",          path: "/admin/token-usage",   icon: Zap },
        { name: "Quick discovery",      path: "/admin/quick-message-discovery", icon: SearchCheck },
        { name: "Templates WA",         path: "/admin/templates",     icon: LayoutTemplate },
        { name: "Email sender",         path: "/admin/email",         icon: Mail },
        { name: "CRM",                  path: "/crm",                 icon: Database },
        { name: "PDPA register",        path: "/admin/pdpa-register", icon: ShieldCheck },
        { name: "Chats admin",          path: "/conversations-admin", icon: MessageCircle },
        { name: "My profile",           path: "/profile",             icon: User },
      ]
    : buildClinicNavKeys({
        permissions,
        enableExperienceIntelligence,
      }).map((key) => clinicNavItemsByKey[key]);

  /* ───────────────────────────────────────────────
     🧭 Rendu
  ─────────────────────────────────────────────── */
  const asideClass =
    variant === "desktop"
      ? "sidebar group/sidebar w-14 hover:w-60 shrink-0 bg-white border-r border-slate-200/80 flex-col h-full hidden lg:flex shadow-[1px_0_3px_0_rgb(0,0,0,0.04)] transition-[width] duration-200 ease-in-out overflow-hidden"
      : "sidebar w-60 bg-white flex flex-col h-full";

  // On desktop, labels/badges fade in on sidebar hover. On mobile, always visible.
  const fadeClass =
    variant === "desktop"
      ? "whitespace-nowrap opacity-0 group-hover/sidebar:opacity-100 transition-opacity duration-150"
      : "whitespace-nowrap";

  const badgeFadeClass =
    variant === "desktop"
      ? "opacity-0 group-hover/sidebar:opacity-100 transition-opacity duration-150"
      : "";

  return (
    <aside className={asideClass}>
      {/* Logo */}
      <div className="px-3 py-4 border-b border-slate-100 shrink-0 flex items-center gap-3">
        <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center shrink-0">
          <span className="text-white font-bold text-sm leading-none">B</span>
        </div>
        <span className={`text-lg font-bold text-primary tracking-tight ${fadeClass}`}>
          Back Office
        </span>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
        {navItems.map(({ name, path, icon: Icon }) => {
          const isActive =
            path === buildClinicSettingsPath("profile")
              ? location.pathname.startsWith("/settings")
              : location.pathname === path;
          return (
            <Link
              key={path}
              to={path}
              title={name}
              onClick={() => onClose?.()}
              className={`relative flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-150
                ${isActive
                  ? "bg-teal-50 text-primary"
                  : "text-slate-500 hover:text-slate-900 hover:bg-slate-100"
                }`}
            >
              <Icon className="w-4 h-4 shrink-0" strokeWidth={isActive ? 2 : 1.75} />
              <span className={`truncate ${fadeClass}`}>{name}</span>

              {/* Badge tâches assignées sur Chats */}
              {name === "Chats" && tasksCount > 0 && (
                <span className={`ml-auto bg-red-500 text-white text-[10px] font-bold rounded-full px-1.5 leading-5 shrink-0 ${badgeFadeClass}`}>
                  {tasksCount > 9 ? "9+" : tasksCount}
                </span>
              )}

              {/* Dot Knowledge base si exemples en attente */}
              {name === "Knowledge base" && hasPendingExamples && (
                <span className={`ml-auto w-2 h-2 rounded-full bg-red-500 shrink-0 ${badgeFadeClass}`} />
              )}
            </Link>
          );
        })}
      </nav>

      {/* Séparateur */}
      <div className="border-t border-slate-100 mx-3 shrink-0" />

      {/* Zone inférieure : statut clinique + bot + user + logout */}
      <div className="px-3 py-3 space-y-2 shrink-0">

        {/* Statut clinique */}
        {user && (
          <div className="relative group/status">
            <button
              onClick={openStatusModal}
              disabled={!clinicStatus || statusLoading}
              className={`w-full inline-flex items-center gap-2 text-xs font-medium px-2.5 py-1.5 rounded-full border transition ${badgeColors.container} disabled:opacity-60 disabled:cursor-not-allowed`}
            >
              <span className={`w-2 h-2 rounded-full shrink-0 ${badgeColors.dot}`} />
              <span className={`truncate ${fadeClass}`}>
                {statusLoading
                  ? "Loading status…"
                  : clinicStatus?.source === "FORCED"
                    ? `Forced ${clinicStatus.status}`
                    : clinicStatus?.label
                        ?.replace(
                          /(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\+\d{2}:\d{2})/,
                          (match) => formatDateHuman(match)
                        ) || "Status unavailable"}
              </span>
            </button>

            {clinicStatus?.source === "FORCED" && clinicStatus.label && (
              <div className="absolute left-0 bottom-full mb-1 z-50 hidden group-hover/status:block w-64 bg-gray-800 text-white text-xs rounded-md px-3 py-2 shadow-lg pointer-events-none">
                {clinicStatus.label}
              </div>
            )}
          </div>
        )}

        {/* Bot toggle */}
        {canToggleClinicBot && (
          <button
            onClick={handleToggleClinicBot}
            disabled={botToggleLoading}
            className={`w-full inline-flex items-center gap-2 text-xs font-medium px-2.5 py-1.5 rounded-full border transition ${
              botDisabledClinic
                ? "bg-red-100 text-red-800 border-red-200"
                : "bg-green-100 text-green-800 border-green-200"
            } disabled:opacity-60 disabled:cursor-not-allowed`}
          >
            <span className={`w-2 h-2 rounded-full shrink-0 ${botDisabledClinic ? "bg-red-500" : "bg-green-500"}`} />
            <span className={fadeClass}>{botToggleLoading ? "Updating Bot…" : `Bot ${botDisabledClinic ? "OFF" : "ON"}`}</span>
          </button>
        )}

        {/* Connected as */}
        {user && (
          <p className={`text-xs text-slate-500 px-1 truncate ${fadeClass}`}>
            {activeStaff ? (
              <>
                <span className="font-semibold text-slate-700">{sidebarIdentityLabel}</span>
                {activeStaff.role && <>{" "}({activeStaff.role})</>}
              </>
            ) : activeDoctor ? (
              <>
                <span className="font-semibold text-slate-700">{sidebarIdentityLabel}</span>
                {" "}({activeDoctor.role})
              </>
            ) : (
              <span className="font-semibold text-slate-700">{sidebarIdentityLabel}</span>
            )}
          </p>
        )}

        {/* Logout */}
        <button
          onClick={handleLogout}
          disabled={loading}
          className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-slate-500 hover:text-red-600 hover:bg-red-50 transition-colors duration-150"
        >
          <LogOut className="w-4 h-4 shrink-0" />
          <span className={fadeClass}>{loading ? "Logging out…" : "Log out"}</span>
        </button>
      </div>

      {/* Modal de statut clinique */}
      {statusModalOpen && clinicStatus && (
        <div
          className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 px-4"
          onClick={() => setStatusModalOpen(false)}
        >
          <div
            className="bg-white rounded-lg shadow-lg w-full max-w-md p-6 space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm text-slate-500">Clinic status</p>
                <p className="text-lg font-semibold text-slate-800">
                  {clinicStatus.label.replace(
                    /(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\+\d{2}:\d{2})/,
                    (match) => formatDateHuman(match)
                  )}
                </p>
              </div>
              <button
                className="text-sm text-slate-500 hover:text-slate-700"
                onClick={() => setStatusModalOpen(false)}
              >
                Close
              </button>
            </div>

            {clinicStatus.source === "SCHEDULE" || clinicStatus.source === "HOLIDAY" ? (
              <div className="space-y-4">
                <div className="flex gap-2">
                  {(["OPEN", "CLOSED"] as OverrideStatus[]).map((opt) => (
                    <button
                      key={opt}
                      onClick={() => setOverrideStatus(opt)}
                      className={`flex-1 border rounded-md px-3 py-2 text-sm font-medium transition ${
                        overrideStatus === opt
                          ? "border-primary text-primary bg-blue-50"
                          : "border-gray-200 text-slate-700 hover:border-gray-300"
                      }`}
                    >
                      {opt === "OPEN" ? "Force open" : "Force closed"}
                    </button>
                  ))}
                </div>

                <div className="space-y-2">
                  <label className="text-xs text-slate-500" htmlFor="override-until">
                    Until <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="override-until"
                    type="datetime-local"
                    value={overrideUntil}
                    onChange={(e) => setOverrideUntil(e.target.value)}
                    className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-xs text-slate-500" htmlFor="override-reason">
                    Reason (optional)
                  </label>
                  <input
                    id="override-reason"
                    type="text"
                    value={overrideReason}
                    onChange={(e) => setOverrideReason(e.target.value)}
                    className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                    placeholder="Add a note"
                  />
                </div>

                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => setStatusModalOpen(false)}
                    className="text-sm px-4 py-2 rounded-md border border-gray-200 text-slate-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleForceStatus}
                    disabled={statusUpdating || !overrideUntil}
                    className="text-sm px-4 py-2 rounded-md bg-primary text-white hover:bg-blue-600 disabled:opacity-60"
                  >
                    {statusUpdating ? "Saving…" : "Update"}
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-slate-600">
                  The clinic status is currently forced. You can return to the automatic schedule.
                </p>
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => setStatusModalOpen(false)}
                    className="text-sm px-4 py-2 rounded-md border border-gray-200 text-slate-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleRevertSchedule}
                    disabled={statusUpdating}
                    className="text-sm px-4 py-2 rounded-md bg-primary text-white hover:bg-blue-600 disabled:opacity-60"
                  >
                    {statusUpdating ? "Saving…" : "Revert to schedule"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </aside>
  );
}
